/**
 * Store Accessor - Singleton pattern to break circular dependencies
 * 
 * This module provides controlled access to the Redux store for modules
 * that would otherwise create circular dependencies.
 * 
 * Pattern: The store is injected after initialization, ensuring proper
 * module loading order while maintaining type safety and error handling.
 */

class StoreAccessor {
  constructor() {
    this._store = null;
    this._isInitialized = false;
  }

  /**
   * Initialize the store accessor with the Redux store
   * @param {Object} store - Redux store instance
   * @throws {Error} If store is already initialized or invalid
   */
  initialize(store) {
    if (this._isInitialized) {
      console.warn('StoreAccessor: Store already initialized');
      return;
    }

    if (!store || typeof store.getState !== 'function') {
      throw new Error('StoreAccessor: Invalid store instance provided');
    }

    this._store = store;
    this._isInitialized = true;
  }

  /**
   * Get the current Redux store
   * @returns {Object|null} Redux store instance or null if not initialized
   */
  getStore() {
    return this._store;
  }

  /**
   * Get the current Redux state
   * @returns {Object|null} Current state or null if store not initialized
   */
  getState() {
    return this._store?.getState() || null;
  }

  /**
   * Check if store is initialized
   * @returns {boolean}
   */
  isInitialized() {
    return this._isInitialized;
  }

  /**
   * Dispatch an action (if store is initialized)
   * @param {Object} action - Redux action
   */
  dispatch(action) {
    if (this._store) {
      this._store.dispatch(action);
    }
  }
}

// Export singleton instance
export const storeAccessor = new StoreAccessor();











